import { memo, useCallback } from "react";
import Close from "~icons/material-symbols/close";
import { css } from "../../../../styled-system/css";
import { t } from "../../../libs/i18n";
import { openUserAuthenticationReregistration } from "../../../libs/sdk";

type OsakiReregistrationModalProps = {
  onClose: () => void;
};

const overlayStyle = css({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  bg: "rgba(0, 0, 0, 0.1)",
  zIndex: 888,
});

const backdropStyle = css({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  zIndex: 999,
  backdropFilter: "blur(3px)",
  transition: "transform 0.3s ease-out",
  transform: "translateY(100%)",
  animation: "slide-up 0.3s forwards",
});

const modalContentStyle = css({
  bg: "white",
  roundedTop: "2xl",
  px: "20px",
  pt: "20px",
  pb: "36px",
  display: "flex",
  flexDir: "column",
  gap: "20px",
  position: "fixed",
  bottom: 0,
  left: 0,
  width: "100%",
  zIndex: 999,
  boxShadow: "0px 4px 12px 0px rgba(0,0,0,0.08)",
});

const headerStyle = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
});

const titleStyle = css({
  color: "text.primary",
  fontSize: "18px",
  fontWeight: 500,
  lineHeight: "1.35",
});

const closeButtonStyle = css({
  color: "text.primary",
  border: "none",
  cursor: "pointer",
  bg: "action.hover",
  rounded: "full",
  p: "4px",
  flexShrink: 0,
});

const descriptionStyle = css({
  color: "text.secondary",
  fontSize: "16px",
  lineHeight: "1.5",
});

const buttonContainerStyle = css({
  display: "flex",
  flexDir: "column",
  gap: "12px",
});

const primaryButtonStyle = css({
  bg: "surface.accentPrimary",
  color: "white",
  rounded: "2xl",
  px: "40px",
  py: "16px",
  fontWeight: 500,
  fontSize: "16px",
  lineHeight: "1.25",
  height: "56px",
});

const secondaryButtonStyle = css({
  bg: "rgba(229, 231, 235, 0.50)",
  rounded: "2xl",
  px: "32px",
  py: "16px",
  fontWeight: 500,
  fontSize: "16px",
  lineHeight: "1.25",
  height: "56px",
  color: "text.primary",
});

export const OsakiReregistrationModal = memo(function OsakiReregistrationModal({
  onClose,
}: OsakiReregistrationModalProps) {
  const handleOutsideClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleProceed = useCallback(() => {
    void openUserAuthenticationReregistration();
    onClose();
  }, [onClose]);

  return (
    <div className={overlayStyle}>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Modal backdrop click handler */}
      <div className={backdropStyle} onClick={handleOutsideClick}>
        <div className={modalContentStyle}>
          <div className={headerStyle}>
            <p className={titleStyle}>{t("住所の確認を行います")}</p>
            <button
              type="button"
              className={closeButtonStyle}
              onClick={onClose}
            >
              <Close />
            </button>
          </div>

          <p className={descriptionStyle}>
            {t(
              "画面の表示に従って、マイナンバーカードの読み込みを行ってください。",
            )}
          </p>

          <div className={buttonContainerStyle}>
            <button
              type="button"
              className={primaryButtonStyle}
              onClick={handleProceed}
            >
              {t("読み込みに進む")}
            </button>
            <button
              type="button"
              className={secondaryButtonStyle}
              onClick={onClose}
            >
              {t("あとで")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});
